import styled from '@emotion/styled';
import { SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import createPersistedState from 'use-persisted-state';
import { ValuesType } from 'utility-types';
import { v4 } from 'uuid';
import { GAME_MODE, SingSetup, SongPreview } from '~/interfaces';
import Box from '~/modules/Elements/AKUI/Primitives/Box';
import { Button } from '~/modules/Elements/Button';
import { Switcher } from '~/modules/Elements/Switcher';
import { nextIndex, nextValue } from '~/modules/Elements/Utils/indexes';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useViewportSize from '~/modules/hooks/useViewportSize';
import isDev from '~/modules/utils/isDev';
import { getAvailableGameModes, resolveGameMode } from './gameModes';

interface Props {
  songPreview: SongPreview;
  onNextStep: (setup: SingSetup) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
  context?: 'page' | 'preview';
}

const gameModeNames = {
  [GAME_MODE.DUEL]: 'Duel',
  [GAME_MODE.PASS_THE_MIC]: 'Pass The Mic',
  [GAME_MODE.CO_OP]: 'Cooperation',
};


const difficultyNames = ['Hard', 'Medium', 'Easy'];

if (isDev()) {
  difficultyNames.push('Debug 4');
  difficultyNames.push('Debug 5');
  difficultyNames.push('Debug 6');
}

const useSetGameMode = createPersistedState<ValuesType<typeof GAME_MODE> | null>('song_settings-game_mode-v3');
const useSetTolerance = createPersistedState<number>('song_settings-tolerance-v2');

export default function GameSettings({
  songPreview,
  onNextStep,
  keyboardControl,
  onExitKeyboardControl,
  context = 'page',
}: Props) {
  const [rememberedMode, setMode] = useSetGameMode(null);
  const availableGameModes = getAvailableGameModes(songPreview.tracksCount);
  const mode = resolveGameMode(songPreview.tracksCount, rememberedMode);
  const modeLocked = availableGameModes.length === 1;
  const [tolerance, setTolerance] = useSetTolerance(1);

  const { width } = useViewportSize();
  const isMobile = (width ?? 9999) < 640;
  const isPreview = context === 'preview';

  const handleNextButton = () => {
    const singSetup: SingSetup = {
      id: v4(),
      players: [],
      mode,
      tolerance: tolerance + 1,
    };
    onNextStep(singSetup);
  };

  const changeMode = () => {
    if (modeLocked) return;
    setMode(nextValue([...availableGameModes], mode));
  };
  const changeTolerance = () => setTolerance((current) => nextIndex(difficultyNames, current, -1));

  const { register } = useKeyboardNav({
    enabled: keyboardControl,
    onBackspace: onExitKeyboardControl,
    additionalHelp: { remote: ['select-song'] },
  });

  return (
    <Scaffold data-mobile={isMobile} data-preview={isPreview}>
      {/* ✅ CONTENIDO */}
      <Content>
        <GSSwitcher
          {...register('difficulty-setting', changeTolerance, 'Change difficulty')}
          label="Difficulty"
          value={difficultyNames[tolerance]}
          data-test-value={difficultyNames[tolerance]}
          data-mobile={isMobile}
        />

        <GSSwitcher
          {...register('game-mode-setting', changeMode, 'Change mode', false, { disabled: modeLocked })}
          disabled={modeLocked}
          label="Mode"
          value={gameModeNames[mode]}
          data-test-value={gameModeNames[mode]}
          data-mobile={isMobile}
        />

        <ModeDescription data-mobile={isMobile}>
          <h3>
            {mode === GAME_MODE.DUEL && 'Face off against each other - person that earns more points wins.'}
            {mode === GAME_MODE.CO_OP &&
              'Join forces and sing together - your points will be added up to a single pool.'}
            {mode === GAME_MODE.PASS_THE_MIC && (
              <>
                For more than 2 players - split into groups and pass the microphone within the group when prompted with{' '}
                <SwapHorizIcon /> symbol.
              </>
            )}
          </h3>
        </ModeDescription>
      </Content>

      {/* ✅ FOOTER FIJO ABAJO */}
      <Footer>
        <PlayButton
          {...register('next-step-button', handleNextButton, undefined, true)}
          data-mobile={isMobile}
          data-preview={isPreview}>
          Next ➤
        </PlayButton>
      </Footer>
    </Scaffold>
  );
}

/* ---------------- STYLES ---------------- */

const Scaffold = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: 0.75rem;
`;

const PlayButton = styled(Button)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 46px;
  padding: 0.8rem 1rem;
  font-size: clamp(16px, 5vw, 22px);
  border-radius: 8px;
`;

const ModeDescription = styled(Box)`
  margin: 0 !important;
  padding: 1rem;
  color: var(--walky-ink);
  background: var(--walky-canvas);
  border: 1px solid var(--walky-border);
  border-radius: 8px;

  h3 {
    margin: 0;
    font-size: clamp(14px, 3.8vw, 20px);
    line-height: 1.2;
    color: var(--walky-ink) !important;
  }
`;

const GSSwitcher = styled(Switcher)`
  && {
    width: 100%;
    min-height: 48px;
    box-sizing: border-box;
    padding: 9px 12px;
    font-size: clamp(18px, 2vw, 30px);
    color: var(--walky-ink);
    background: var(--walky-surface-raised);
    border: 1px solid var(--walky-border);
    border-radius: 8px;
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 640px) {
    && {
      min-height: 46px;
      padding: 10px 12px;
      font-size: clamp(16px, 4.8vw, 20px);
    }
  }

  &&[data-disabled='true'] {
    opacity: 0.72;
    cursor: default;
  }

  &&[data-disabled='true']:hover {
    border-color: var(--walky-border);
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.06);
  }
`;
